import React from 'react';
import { Card, Space, Typography, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../../components/common/Button';

const { Title, Text } = Typography;

async function clearIndexedDb(storageKey: string = 'rq-cache', dbName: string = 'RQCacheDB', storeName: string = 'rq_cache_store') {
  return new Promise<void>((resolve, reject) => {
    const openReq = indexedDB.open(dbName, 1);
    openReq.onupgradeneeded = (event: any) => {
      const db: IDBDatabase = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'key' });
      }
    };
    openReq.onsuccess = (event: any) => {
      const db: IDBDatabase = event.target.result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const delReq = store.delete(storageKey);
      delReq.onsuccess = () => {};
      delReq.onerror = () => reject(delReq.error);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => {
        const err: any = (tx as any).error;
        db.close();
        reject(err);
      };
      tx.onabort = () => {
        const err: any = (tx as any).error || new Error('Transaction aborted');
        db.close();
        reject(err);
      };
    };
    openReq.onerror = () => reject(openReq.error);
  });
}

function clearLocalStorageStreams(prefix: string = 'stream:') {
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith(prefix)) toDelete.push(k);
    }
    toDelete.forEach(k => localStorage.removeItem(k));
    return { removed: toDelete.length };
  } catch (e) {
    throw e;
  }
}

async function clearIndexedDbStreams(prefix: string = 'stream:', dbName: string = 'RQCacheDB', storeName: string = 'rq_cache_store') {
  return new Promise<{ removed: number }>((resolve, reject) => {
    const openReq = indexedDB.open(dbName, 1);
    openReq.onupgradeneeded = (event: any) => {
      const db: IDBDatabase = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'key' });
      }
    };
    openReq.onsuccess = (event: any) => {
      const db: IDBDatabase = event.target.result;
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      let removed = 0;
      const req = store.openCursor();
      req.onsuccess = (e: any) => {
        const cursor: IDBCursorWithValue | null = e.target.result;
        if (cursor) {
          const k = (cursor.value && cursor.value.key) || cursor.key;
          if (typeof k === 'string' && k.startsWith(prefix)) {
            const del = store.delete(k);
            del.onsuccess = () => { removed += 1; cursor.continue(); };
            del.onerror = () => { reject(del.error); };
          } else {
            cursor.continue();
          }
        }
      };
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => { db.close(); resolve({ removed }); };
      tx.onerror = () => { const err: any = (tx as any).error; db.close(); reject(err); };
      tx.onabort = () => { const err: any = (tx as any).error || new Error('Transaction aborted'); db.close(); reject(err); };
    };
    openReq.onerror = () => reject(openReq.error);
  });
}

export default function CacheMaintenance() {
  const [busy, setBusy] = React.useState(false);
  const queryClient = useQueryClient();

  // Bellekteki RQ cache temizlenmeden IDB silmek yetmez: persist.ts her sorgu
  // hareketinde ve beforeunload'da bellekteki state'i IDB'ye geri yazar.
  // Bu yuzden once bellek bosaltilir, sonra IDB silinir, sonra sayfa yenilenir.
  const reloadSoon = () => {
    window.setTimeout(() => window.location.reload(), 600);
  };

  const onClearRQ = async () => {
    if (busy) return;
    setBusy(true);
    try {
      queryClient.clear();
      await clearIndexedDb('rq-cache', 'RQCacheDB', 'rq_cache_store');
      message.success('React Query cache cleared (memory + persisted). Reloading...');
      reloadSoon();
    } catch (e: any) {
      message.error(e?.message || 'Failed to clear persisted cache');
      setBusy(false);
    }
  };

  const onClearStreams = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const ls = clearLocalStorageStreams('stream:');
      const idb = await clearIndexedDbStreams('stream:', 'RQCacheDB', 'rq_cache_store');
      message.success(`Cleared streaming caches: IDB ${idb.removed}, localStorage ${ls.removed}. Reloading...`);
      reloadSoon();
    } catch (e: any) {
      message.error(e?.message || 'Failed to clear streaming caches');
      setBusy(false);
    }
  };

  const onClearAll = async () => {
    if (busy) return;
    setBusy(true);
    try {
      queryClient.clear();
      const ls = clearLocalStorageStreams('stream:');
      const idb = await clearIndexedDbStreams('stream:', 'RQCacheDB', 'rq_cache_store');
      await clearIndexedDb('rq-cache', 'RQCacheDB', 'rq_cache_store');
      message.success(`Cleared RQ cache (memory + persisted) and streaming caches: IDB ${idb.removed}, localStorage ${ls.removed}. Reloading...`);
      reloadSoon();
    } catch (e: any) {
      message.error(e?.message || 'Failed to clear caches');
      setBusy(false);
    }
  };

  return (
    <Card style={{ maxWidth: 720, margin: '0 auto' }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Title level={4} style={{ marginBottom: 0 }}>Cache Maintenance</Title>
        <Text type="secondary">Manage persisted caches stored in the browser.</Text>
        <Space wrap>
          <Button onClick={onClearRQ} loading={busy}>Clear React Query Cache</Button>
          <Button onClick={onClearStreams} loading={busy}>Clear Streaming Caches</Button>
          <Button type="primary" danger onClick={onClearAll} loading={busy}>Clear All</Button>
        </Space>
      </Space>
    </Card>
  );
}
